import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

function toneForResult({ created = 0, waitlist = 0, duplicates = 0, errors = [] }) {
    if (errors.length > 0 && created === 0 && waitlist === 0) return 'error';
    if (created === 0 && waitlist === 0 && duplicates > 0) return 'duplicatesOnly';
    if (created === 0 && waitlist === 0 && errors.length === 0) return 'empty';
    if (duplicates > 0 || waitlist > 0 || errors.length > 0) return 'partial';
    return 'success';
}

export default function BulkEnrollmentResultModal({ isOpen, result, onClose }) {
    const { t } = useTranslation();

    if (!isOpen || !result) return null;

    const created = result.created || 0;
    const waitlist = result.waitlist || 0;
    const duplicates = result.duplicates || 0;
    const errors = result.errors || [];
    const tone = toneForResult({ created, waitlist, duplicates, errors });

    const titleKey =
        tone === 'duplicatesOnly'
            ? 'gym.bulkEnrollment.result.titleDuplicatesOnly'
            : tone === 'error'
              ? 'gym.bulkEnrollment.result.titleError'
              : tone === 'empty'
                ? 'gym.bulkEnrollment.result.titleEmpty'
                : 'gym.bulkEnrollment.result.title';

    const subtitleKey =
        tone === 'duplicatesOnly'
            ? 'gym.bulkEnrollment.result.subtitleDuplicatesOnly'
            : tone === 'error'
              ? 'gym.bulkEnrollment.result.subtitleError'
              : tone === 'partial'
                ? 'gym.bulkEnrollment.result.subtitlePartial'
                : tone === 'empty'
                  ? 'gym.bulkEnrollment.result.subtitleEmpty'
                  : 'gym.bulkEnrollment.result.subtitleSuccess';

    const Icon =
        tone === 'duplicatesOnly' || tone === 'empty'
            ? Info
            : tone === 'error'
              ? AlertCircle
              : CheckCircle2;

    const iconWrapClass =
        tone === 'duplicatesOnly' || tone === 'empty'
            ? 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/70'
            : tone === 'error'
              ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';

    const chips = [
        {
            key: 'created',
            count: created,
            label: t('gym.bulkEnrollment.result.chipCreated', { count: created }),
            className:
                'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-200',
        },
        {
            key: 'waitlist',
            count: waitlist,
            label: t('gym.bulkEnrollment.result.chipWaitlist', { count: waitlist }),
            className:
                'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200',
        },
        {
            key: 'duplicates',
            count: duplicates,
            label: t('gym.bulkEnrollment.result.chipDuplicates', { count: duplicates }),
            className:
                'border-gray-200 bg-gray-50 text-gray-700 dark:border-white/15 dark:bg-white/5 dark:text-white/75',
        },
        {
            key: 'errors',
            count: errors.length,
            label: t('gym.bulkEnrollment.result.chipErrors', { count: errors.length }),
            className:
                'border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-200',
        },
    ].filter((chip) => chip.count > 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-900">
                <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-white/10">
                    <div className="flex items-start gap-3">
                        <span className={`mt-0.5 rounded-lg p-2 ${iconWrapClass}`}>
                            <Icon size={20} aria-hidden />
                        </span>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t(titleKey)}
                            </h3>
                            <p className="mt-1 text-sm text-gray-600 dark:text-white/70">
                                {t(subtitleKey)}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10"
                        aria-label={t('gym.bulkEnrollment.result.close')}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-4 px-5 py-5">
                    {chips.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {chips.map((chip) => (
                                <span
                                    key={chip.key}
                                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${chip.className}`}
                                >
                                    {chip.label}
                                </span>
                            ))}
                        </div>
                    )}

                    {errors.length > 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                            {errors[0]}
                            {errors.length > 1
                                ? ` ${t('gym.bulkEnrollment.result.moreErrors', {
                                      count: errors.length - 1,
                                  })}`
                                : ''}
                        </p>
                    )}

                    <p className="text-xs text-gray-500 dark:text-white/55">
                        {t('gym.bulkEnrollment.result.refreshHint')}
                    </p>
                </div>

                <div className="border-t border-gray-200 px-5 py-4 dark:border-white/10">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                        >
                            {t('gym.bulkEnrollment.result.close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
